import { Injectable } from "@angular/core";
import {
  ExportService,
  ExportFileFormat,
  ExportRange,
  ExportContext,
} from "app/layout/export-overlay";
import { takeUntil } from "rxjs/operators";
import ceil from "lodash-es/ceil";
import { 
  WorkProgressMaterialItem, 
  WorkProgressMaterialsResponse,
  WORK_PROGRESS_MATERIAL_EXPORT_ID,
  WORK_PROGRESS_MATERIAL_TITLE,
  WORK_PROGRESS_MATERIAL_PROPERTIES
} from "../interfaces/work-progress-material.interface";
import { PrincipalScreenService } from "../../principal-screen/principal-screen.service";
import { IPagination } from "app/common/interfaces";
import { OrderAttributes } from "@albatrosdeveloper/ave-models-package";

@Injectable()
export class WorkProgressMaterialExport {
  constructor(
    private exportService: ExportService,
    private principalScreenService: PrincipalScreenService
  ) {}

  private transformWorkProgressMaterials(materials: WorkProgressMaterialItem[]) {
    return materials.map(material => ({
      materialCode: material.materialCode || '',
      materialName: material.materialName || '',
      clientCode: material.clientCode || '',
      clientName: material.clientName || '',
      documentNumber: material.documentNumber || '',
      documentClass: material.documentClass || '',
      baseQuantity: material.baseQuantity || 0,
      baseUnitOfMeasure: material.baseUnitOfMeasure
        ? `${material.baseUnitOfMeasure.code} - ${material.baseUnitOfMeasure.name}`
        : '',
      parallelQuantity: material.parallelQuantity || 0,
      parallelUnitOfMeasure: material.parallelUnitOfMeasure
        ? `${material.parallelUnitOfMeasure.code} - ${material.parallelUnitOfMeasure.name}`
        : '',
      total: material.total || 0,
      hasBatches: material.hasBatches ? 'Sí' : 'No',
      batchCount: material.batchCount || 0,
      batchNumber: material.batches.map(b => b.batchNumber).join('; '),
      batchQuantity: material.batches.map(b => b.quantity).join('; '),
      expirationDate: material.batches.map(b => b.expirationDate || 'N/A').join('; '),
      manufacturingDate: material.batches.map(b => b.manufacturingDate || 'N/A').join('; ')
    }));
  }

  /**
   * Exporta todos los materiales de trabajo en curso
   */
  export(
    format: ExportFileFormat,
    range: ExportRange,
    tripId: number,
    materials?: WorkProgressMaterialItem[]
  ) {
    // Si ya tenemos los materiales, exportarlos directamente
    if (materials && materials.length > 0) {
      const title = range === ExportRange.CurrentPage 
        ? `${WORK_PROGRESS_MATERIAL_TITLE} - Página Actual`
        : WORK_PROGRESS_MATERIAL_TITLE;
      this.exportLocal(format, materials, title);
      return;
    }

    // Si no tenemos los materiales, obtenerlos del servicio
    this.exportFromService(format, tripId);
  }

  /**
   * Exporta materiales ya cargados en memoria (datos locales)
   */
  exportLocal(format: ExportFileFormat, materials: WorkProgressMaterialItem[], customTitle?: string) {
    const title = customTitle || WORK_PROGRESS_MATERIAL_TITLE;
    this.exportService
      .task({
        id: WORK_PROGRESS_MATERIAL_EXPORT_ID,
        title: title,
        format: format,
        iterations: 1,
        start: (ctx: ExportContext) => {
          ctx.processor.localizeItemProperties(
            title,
            WORK_PROGRESS_MATERIAL_PROPERTIES
          );
        },
        next: (ctx: ExportContext) => {
          const transformedData = this.transformWorkProgressMaterials(materials);
          
          ctx.processor.addToSection(
            title,
            transformedData
          );
          
          ctx.next();
        },
      })
      .start();
  }

  /**
   * Exporta obteniendo los materiales del servicio
   */
  private exportFromService(format: ExportFileFormat, tripId: number) {
    this.exportService
      .task({
        id: WORK_PROGRESS_MATERIAL_EXPORT_ID,
        title: WORK_PROGRESS_MATERIAL_TITLE,
        format: format,
        iterations: 1,
        start: (ctx: ExportContext) => {
          ctx.processor.localizeItemProperties(
            WORK_PROGRESS_MATERIAL_TITLE,
            WORK_PROGRESS_MATERIAL_PROPERTIES
          );
        },
        next: (ctx: ExportContext) => {
          this.principalScreenService.getWorkInProgressMaterialsComplete(tripId)
            .then((response: WorkProgressMaterialsResponse) => {
              const transformedData = this.transformWorkProgressMaterials(response.materials);
              
              ctx.processor.addToSection(
                WORK_PROGRESS_MATERIAL_TITLE,
                transformedData
              );
              
              ctx.next();
            })
            .catch((error) => {
              ctx.next();
            });
        },
      })
      .start();
  }

  /**
   * Exporta un material específico con sus lotes
   */
  exportSingleMaterial(
    format: ExportFileFormat,
    material: WorkProgressMaterialItem
  ) {
    this.exportService
      .task({
        id: `${WORK_PROGRESS_MATERIAL_EXPORT_ID}-single`,
        title: `${WORK_PROGRESS_MATERIAL_TITLE} - ${material.materialName}`,
        format: format,
        iterations: 1,
        start: (ctx: ExportContext) => {
          ctx.processor.localizeItemProperties(
            WORK_PROGRESS_MATERIAL_TITLE,
            WORK_PROGRESS_MATERIAL_PROPERTIES
          );
        },
        next: (ctx: ExportContext) => {
          const transformedData = this.transformWorkProgressMaterials([material]);
          
          ctx.processor.addToSection(
            WORK_PROGRESS_MATERIAL_TITLE,
            transformedData
          );
          
          ctx.next();
        },
      })
      .start();
  }

  /**
   * Exporta solo los lotes de un material específico
   */
  exportMaterialBatches(
    format: ExportFileFormat,
    material: WorkProgressMaterialItem
  ) {
    if (!material.hasBatches || material.batches.length === 0) {
      return;
    }

    const batchData = material.batches.map(batch => ({
      materialCode: material.materialCode,
      materialName: material.materialName,
      clientCode: material.clientCode,
      clientName: material.clientName,
      documentNumber: material.documentNumber,
      batchNumber: batch.batchNumber,
      quantity: batch.quantity,
      expirationDate: batch.expirationDate || 'N/A',
      manufacturingDate: batch.manufacturingDate || 'N/A',
      status: batch.status || 'N/A',
      warehouse: batch.warehouse ? JSON.stringify(batch.warehouse) : 'N/A'
    }));

    this.exportService
      .task({
        id: `${WORK_PROGRESS_MATERIAL_EXPORT_ID}-batches`,
        title: `Lotes - ${material.materialName}`,
        format: format,
        iterations: 1,
        start: (ctx: ExportContext) => {
          ctx.processor.localizeItemProperties(
            'Lotes del Material',
            {
              materialCode: 'Código Material',
              materialName: 'Nombre Material',
              clientCode: 'Código Cliente',
              clientName: 'Cliente',
              documentNumber: 'Nro Documento',
              batchNumber: 'Número de Lote',
              quantity: 'Cantidad',
              expirationDate: 'Fecha Vencimiento',
              manufacturingDate: 'Fecha Fabricación',
              status: 'Estado',
              warehouse: 'Almacén'
            }
          );
        },
        next: (ctx: ExportContext) => {
          ctx.processor.addToSection(
            'Lotes del Material',
            batchData
          );
          
          ctx.next();
        },
      })
      .start();
  }

  canStartTask() {
    if (this.exportService.taskIsActive(WORK_PROGRESS_MATERIAL_EXPORT_ID)) {
      this.exportService.setVisible(true);
      return false;
    }
    
    return true;
  }
}